import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { AuthService } from '../../../../core/auth/auth.service';
import { ChatStateService, ChatWidgetView } from '../../services/chat-state.service';
import { ChatConversation, ChatIconKind, ChatMessage, ChatNotification } from '../../models/chat.model';
import { NewConversationDialogComponent, NewConversationResult } from '../new-conversation-dialog/new-conversation-dialog.component';

@Component({
  selector: 'app-chat-widget',
  templateUrl: './chat-widget.component.html',
  styleUrls: ['./chat-widget.component.sass'],
})
export class ChatWidgetComponent implements OnInit, OnDestroy {
  readonly view$: Observable<ChatWidgetView> = this.state.view$;
  readonly activeConversationId$ = this.state.activeConversationId$;
  readonly badge$ = this.state.badge$;
  readonly conversations$ = this.state.conversations$;
  readonly messages$ = this.state.messages$;
  readonly notifications$ = this.state.notifications$;
  readonly freshIds$ = this.state.freshIds$;
  readonly showInactive$ = this.state.showInactive$;

  // Wspolny kontener na wiadomosci ORAZ powiadomienia (ta sama klasa w obu galeziach szablonu,
  // *ngIf renderuje w danej chwili tylko jedna z nich) - do przewijania na dol po swiezym
  // otwarciu (29.09.2026: "powinienem byc od razu na dole na najnowszych").
  @ViewChild('messagesContainer') private messagesContainerRef?: ElementRef<HTMLDivElement>;

  // "Wymus scroll na dol przy nastepnej emisji" - ustawiane tuz przed akcja, ktora ZAWSZE ma
  // zjechac na dol niezaleznie od tego, gdzie user aktualnie ma scroll (swieze otwarcie,
  // wyslanie wlasnej wiadomosci). Poza tym (np. nowa wiadomosc doszla w tle przez 5s polling)
  // scrollujemy TYLKO jesli user i tak juz byl blisko dolu - inaczej wyrywalibysmy mu widok
  // co 5s w trakcie czytania starszej historii (29.09.2026: "jak ktos do mnie napisze, a mam
  // otwarte okienko, to scroll tez ma zjechac na dol").
  private forceScrollOnNextMessages = false;
  private forceScrollOnNextNotifications = false;

  draft = '';
  editingMessageId: number | null = null;
  editingDraft = '';

  constructor(
    private readonly state: ChatStateService,
    private readonly auth: AuthService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.state.init();

    this.messages$.subscribe(() => {
      if (this.forceScrollOnNextMessages || this.isNearBottom()) {
        this.forceScrollOnNextMessages = false;
        this.scrollToBottomSoon();
      }
    });

    this.notifications$.subscribe(() => {
      if (this.forceScrollOnNextNotifications || this.isNearBottom()) {
        this.forceScrollOnNextNotifications = false;
        this.scrollToBottomSoon();
      }
    });
  }

  ngOnDestroy(): void {
    // celowo NIC - ChatStateService jest singletonem (providedIn:'root'), watek badge'a musi
    // zyc dluzej niz ten komponent (jest jeden na cala aplikacje, montowany raz w MainComponent,
    // ale gdyby kiedys byl w drzewie z krotszym cyklem zycia, nie chcemy zabijac globalnego pollingu).
  }

  get myProfilId(): number | null {
    return this.state.profilId;
  }

  toggleWidget(): void {
    this.state.toggleWidget();
  }

  collapseToClosed(): void {
    this.state.collapseToClosed();
  }

  toggleNotifications(): void {
    this.forceScrollOnNextNotifications = true;
    this.state.toggleNotifications();
  }

  closeNotifications(): void {
    this.state.closeNotifications();
  }

  closeConversation(): void {
    this.editingMessageId = null;
    this.draft = '';
    this.state.closeConversation();
  }

  selectConversation(id: number): void {
    this.editingMessageId = null;
    this.draft = '';
    this.forceScrollOnNextMessages = true;
    this.state.selectConversation(Number(id));
  }

  toggleShowInactive(): void {
    this.state.toggleShowInactive();
  }

  openNewConversationDialog(): void {
    const ref = this.dialog.open<NewConversationDialogComponent, unknown, NewConversationResult>(NewConversationDialogComponent, { width: '420px' });

    ref.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      if (result.kind === 'direct') {
        this.state.createDirectAndOpen(result.profilId);
      } else if (result.kind === 'group') {
        this.state.createGroupAndOpen(result.name, result.profilIds);
      }
    });
  }

  openAddParticipantDialog(conversation: ChatConversation): void {
    const excluded = new Set([this.myProfilId, ...conversation.participants.map((p) => p.profilId)]);
    const ref = this.dialog.open<NewConversationDialogComponent, unknown, NewConversationResult>(NewConversationDialogComponent, {
      width: '420px',
      data: { mode: 'add-participant', excludedProfilIds: [...excluded] },
    });

    ref.afterClosed().subscribe((result) => {
      if (!result || result.kind !== 'add-participant') {
        return;
      }

      this.state.addParticipant(conversation.id, result.profilId, result.showFullHistory);
    });
  }

  sendMessage(): void {
    const id = this.activeConversationId$.value;

    if (!id || !this.draft.trim()) {
      return;
    }

    // Wlasna wyslana wiadomosc tez ma zjechac scroll na dol - inaczej po kilku wiadomosciach
    // pod rzad widok zostaje w tyle i user przestaje widziec to, co sam wlasnie napisal
    // (zgloszenie Patryka 29.09.2026).
    this.forceScrollOnNextMessages = true;
    this.state.sendMessage(id, this.draft);
    this.draft = '';
  }

  startEdit(message: ChatMessage): void {
    this.editingMessageId = message.id;
    this.editingDraft = message.body ?? '';
  }

  cancelEdit(): void {
    this.editingMessageId = null;
    this.editingDraft = '';
  }

  saveEdit(): void {
    if (this.editingMessageId == null || !this.editingDraft.trim()) {
      return;
    }

    this.state.editMessage(this.editingMessageId, this.editingDraft, (ok) => {
      if (ok) {
        this.editingMessageId = null;
        this.editingDraft = '';
      }
    });
  }

  deleteMessage(message: ChatMessage): void {
    this.state.deleteMessage(message.id);
  }

  acknowledgeNotification(notification: ChatNotification, event: Event): void {
    event.stopPropagation();
    this.state.acknowledgeNotification(notification.id);
  }

  goToLink(link: string | null, event: Event): void {
    if (!link) {
      return;
    }

    event.stopPropagation();
    this.collapseToClosed();
    this.router.navigateByUrl(link);
  }

  // ── Prezentacja ──────────────────────────────────────────────────────

  iconKind(conversation: ChatConversation): ChatIconKind {
    return conversation.isGroup ? 'group' : 'direct';
  }

  // Oparte na badge$ (odpytywanym co 30s niezaleznie od tego, czy panel powiadomien byl juz
  // otwarty) - zeby dzwonek pulsowal juz w stanie ZAMKNIETYM, a nie dopiero po pierwszym
  // wczytaniu notifications$ (29.09.2026, dzwonek wydzielony jako osobna ikonka).
  hasUnackedSpecial(): boolean {
    return (this.badge$.value.pendingAck ?? 0) > 0;
  }

  unackedSpecialCount(): number {
    return this.badge$.value.pendingAck ?? 0;
  }

  isFresh(id: number): boolean {
    return this.freshIds$.value.has(id);
  }

  authorLabel(conversation: ChatConversation | undefined, authorId: number): string {
    if (!conversation) {
      return '';
    }

    if (authorId === this.myProfilId) {
      return '';
    }

    const p = conversation.participants.find((x) => x.profilId === authorId);
    return p ? `${p.imie} ${p.nazwisko}` : '';
  }

  activeConversation(): ChatConversation | undefined {
    const id = this.activeConversationId$.value;
    return this.conversations$.value.find((c) => c.id === id);
  }

  /** "13692 · 6952344248805 · Godox IM10" -> wiersz produktu; inne linie -> zwykly tekst.
   *  Ta sama logika co dawny dzwonek (messages-bell), zeby powiadomienia bulk-import
   *  nie stracily czytelnosci po przeniesieniu do komunikatora. */
  notificationRows(n: ChatNotification): { id?: number; ean?: string; name?: string; text?: string }[] {
    return (n.body ?? '').split('\n').map((l) => {
      const mm = l.match(/^(\d+) · (.*?) · (.*)$/);
      return mm ? { id: Number(mm[1]), ean: mm[2], name: mm[3] } : { text: l };
    });
  }

  goProduct(id: number, event: Event): void {
    event.stopPropagation();
    this.collapseToClosed();
    this.router.navigate(['/produkty/edit', id]);
  }

  // trackBy dla *ngFor list odswiezanych pollingiem (5s przy otwartym czacie) - bez tego kazdy
  // poll burzy i odtwarza CALY DOM listy (nowa tablica z API = nowe referencje obiektow), co
  // niszczy focus/stan otwartego pola edycji w trakcie pisania (znalezione przy testach 28.09.2026).
  trackByMessageId(_: number, message: ChatMessage): number {
    return message.id;
  }

  trackByNotificationId(_: number, notification: ChatNotification): number {
    return notification.id;
  }

  trackByConversationId(_: number, conversation: ChatConversation): number {
    return conversation.id;
  }

  // Wolane SYNCHRONICZNIE w callbacku subskrypcji, zanim Angular zdazy przerenderowac *ngFor
  // z nowymi danymi - kontener wciaz ma wymiary SPRZED aktualizacji, czyli dokladnie to, co
  // trzeba: "czy user byl przy dole PRZED przyjsciem nowej tresci". Brak kontenera (widget
  // zamkniety/inny widok) traktujemy jako "tak" - i tak zaraz nastapi pelne swieze otwarcie.
  private isNearBottom(): boolean {
    const el = this.messagesContainerRef?.nativeElement;
    if (!el) {
      return true;
    }
    const threshold = 80;
    return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
  }

  // setTimeout(0) - dane z API renderuja sie do DOM dopiero po nastepnym cyklu detekcji zmian,
  // wiec odczyt scrollHeight od razu po next() dalby jeszcze wysokosc SPRZED nowej tresci.
  private scrollToBottomSoon(): void {
    setTimeout(() => {
      const el = this.messagesContainerRef?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }
}
